import React from 'react'
import { useFormikContext } from 'formik'
import { useSelector, useDispatch } from 'react-redux'
import {
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  Box,
} from '@mui/material'
import { saveTemporalFeatures } from '../../../redux/slices/Tree/treeFeatures'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import NatureIcon from '@mui/icons-material/Nature'
import GradeIcon from '@mui/icons-material/Grade'
import BugReportIcon from '@mui/icons-material/BugReport'
import CoronavirusIcon from '@mui/icons-material/Coronavirus'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import GrassIcon from '@mui/icons-material/Grass'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import LayersIcon from '@mui/icons-material/Layers'
import AltRouteIcon from '@mui/icons-material/AltRoute'
import SpaIcon from '@mui/icons-material/Spa'
import FilterVintageIcon from '@mui/icons-material/FilterVintage'
import ParkIcon from '@mui/icons-material/Park'
import ItemsContainer from '../../ItemsContainer'
import InconvenienceAutoSelect from '../components/InconvenienceAutoSelect'
import SliderField from '../components/SliderField'
import QualityChipSelector from '../components/QualityChipSelector'
import QualitySummaryBar from '../components/QualitySummaryBar'

const pickItems = (items, names) =>
  names.map(name => items.find(i => i.name === name)).filter(Boolean)

const SectionHeader = ({ icon: Icon, label }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    <Icon sx={{ fontSize: 20, color: 'primary.main' }} />
    <Typography variant="subtitle2" color="primary" fontWeight={600}>
      {label}
    </Typography>
  </Stack>
)

const getColorForOption = label => {
  const lower = (label || '').toLowerCase()
  if (lower.includes('buen') || lower.includes('good') || lower === 'simétrica' || lower === 'simetrica') return '#4caf50'
  if (lower.includes('mal') || lower.includes('bad') || lower.includes('poor') || lower.includes('irregular')) return '#f44336'
  if (lower.includes('regular') || lower.includes('fair') || lower.includes('poco sim')) return '#ff9800'
  return '#9e9e9e'
}

const COLOR_ORDER = { '#4caf50': 0, '#ff9800': 1, '#f44336': 2, '#9e9e9e': 3 }

const convertOptionsToColor = options => {
  if (!options || typeof options !== 'object') return []
  return Object.entries(options)
    .map(([value, label]) => ({
      value,
      label,
      color: getColorForOption(label),
    }))
    .sort((a, b) => (COLOR_ORDER[a.color] ?? 9) - (COLOR_ORDER[b.color] ?? 9))
}

const DEFAULT_OPTIONS = [
  { value: 'Buena', label: 'Buena', color: '#4caf50' },
  { value: 'Regular', label: 'Regular', color: '#ff9800' },
  { value: 'Mala', label: 'Mala', color: '#f44336' },
]

const QUALITY_GROUPS = [
  {
    groupLabel: 'ESTRUCTURA',
    fields: [
      { name: 'calidadRaiz', label: 'Raíz', icon: GrassIcon },
      { name: 'calidadFuste', label: 'Fuste', icon: AccountTreeIcon },
      { name: 'calidadCorteza', label: 'Corteza', icon: LayersIcon },
      { name: 'calidadRamas', label: 'Ramas', icon: AltRouteIcon },
    ],
  },
  {
    groupLabel: 'FOLLAJE',
    fields: [
      { name: 'calidadHojas', label: 'Hojas', icon: SpaIcon },
      { name: 'calidadCima', label: 'Cima', icon: FilterVintageIcon },
      { name: 'calidadCopa', label: 'Copa', icon: ParkIcon },
    ],
  },
]

const ALL_QUALITY_FIELDS = QUALITY_GROUPS.flatMap(g => g.fields)
const ORDERED_FIELD_NAMES = ALL_QUALITY_FIELDS.map(f => f.name)

const PhytosanitaryStep = ({ itemsAttributes, itemsInconvenience, inconvenienceErrors = {} }) => {
  const { values, setFieldValue } = useFormikContext()
  const dispatch = useDispatch()
  const fieldRefs = React.useRef({})

  const temporal = useSelector(state => state.treeFeatures.temporal)
  const hasDiseasePresence = temporal.some(item => item.category === 2)
  const hasPestPresence = temporal.some(item => item.category === 4)

  // Limpiar ubicación de enfermedades cuando no hay presencia
  React.useEffect(() => {
    if (!hasDiseasePresence) {
      const cleaned = temporal.filter(item => item.category !== 3)
      if (cleaned.length !== temporal.length) dispatch(saveTemporalFeatures(cleaned))
    }
  }, [hasDiseasePresence]) // eslint-disable-line react-hooks/exhaustive-deps

  // Limpiar ubicación de plagas cuando no hay presencia
  React.useEffect(() => {
    if (!hasPestPresence) {
      const cleaned = temporal.filter(item => item.category !== 5)
      if (cleaned.length !== temporal.length) dispatch(saveTemporalFeatures(cleaned))
    }
  }, [hasPestPresence]) // eslint-disable-line react-hooks/exhaustive-deps

  const phenologyItems = pickItems(itemsAttributes, ['madurez', 'floracion', 'fructificacion'])

  const qualityItemsMap = React.useMemo(() => {
    const map = {}
    if (!itemsInconvenience) return map
    itemsInconvenience
      .filter(item => item.category === 1)
      .forEach(item => {
        map[item.name] = item
      })
    return map
  }, [itemsInconvenience])

  const getOptionsForField = React.useCallback(
    name => {
      const inconvItem = qualityItemsMap[name]
      return inconvItem ? convertOptionsToColor(inconvItem.options) : DEFAULT_OPTIONS
    },
    [qualityItemsMap],
  )

  const getOptionLabel = React.useCallback(
    (fieldName, value) => {
      const options = getOptionsForField(fieldName)
      const found = options.find(o => String(o.value) === String(value))
      return found?.label ?? (value != null ? String(value) : '')
    },
    [getOptionsForField],
  )

  const handleQualityChange = React.useCallback(
    (fieldName, value) => {
      setFieldValue(fieldName, value)
      const currentIdx = ORDERED_FIELD_NAMES.indexOf(fieldName)
      if (currentIdx < ORDERED_FIELD_NAMES.length - 1) {
        const nextFieldName = ORDERED_FIELD_NAMES[currentIdx + 1]
        setTimeout(() => {
          fieldRefs.current[nextFieldName]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }, 150)
      }
    },
    [setFieldValue],
  )

  const handleMarkAllGood = React.useCallback(() => {
    ALL_QUALITY_FIELDS.forEach(({ name }) => {
      const options = getOptionsForField(name)
      const goodOption = options.find(o => o.color === '#4caf50')
      setFieldValue(name, goodOption?.value || 'Buena')
    })
  }, [getOptionsForField, setFieldValue])

  return (
    <Stack spacing={2.5}>
      {/* Foliage Slider */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={NatureIcon} label="Follaje" />
          <Box sx={{ px: 1 }}>
            <SliderField
              name="follaje"
              label="Porcentaje de follaje"
              min={0}
              max={100}
              step={5}
              value={Number(values.follaje) || 0}
              onChange={(name, val) => setFieldValue(name, val)}
              icon={NatureIcon}
              unit="%"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Phenology */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={HealthAndSafetyIcon} label="Fenología" />
          <Grid container spacing={2}>
            {phenologyItems.map(item => (
              <ItemsContainer key={item.name} {...item} x={12} s={4} m={4} editable />
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Quality by Part */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={GradeIcon} label="Calidad por Partes" />

          <QualitySummaryBar
            qualityFields={ALL_QUALITY_FIELDS}
            values={values}
            onMarkAllGood={handleMarkAllGood}
            getOptionLabel={getOptionLabel}
          />

          {QUALITY_GROUPS.map(({ groupLabel, fields }, groupIdx) => (
            <Box key={groupLabel} sx={{ mb: groupIdx < QUALITY_GROUPS.length - 1 ? 3 : 0 }}>
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  mb: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 0.5,
                }}
              >
                {groupLabel}
              </Typography>

              <Stack spacing={2}>
                {fields.map(({ name, label, icon }) => (
                  <QualityChipSelector
                    key={name}
                    ref={el => {
                      fieldRefs.current[name] = el
                    }}
                    label={label}
                    icon={icon}
                    options={getOptionsForField(name)}
                    selected={values[name] || ''}
                    onChange={val => handleQualityChange(name, val)}
                    isComplete={Boolean(values[name])}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Diseases */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={CoronavirusIcon} label="Enfermedades" />
          <Stack spacing={2.5}>
            <InconvenienceAutoSelect categoryId={2} label="Presencia" editable error={inconvenienceErrors[2]} />
            {hasDiseasePresence && (
              <InconvenienceAutoSelect categoryId={3} label="Ubicación" editable required error={inconvenienceErrors[3]} />
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Pests */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={BugReportIcon} label="Plagas" />
          <Stack spacing={2.5}>
            <InconvenienceAutoSelect categoryId={4} label="Presencia" editable error={inconvenienceErrors[4]} />
            {hasPestPresence && (
              <InconvenienceAutoSelect categoryId={5} label="Ubicación" editable required error={inconvenienceErrors[5]} />
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Physical Damage */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={WarningAmberIcon} label="Problemas Físicos" />
          <InconvenienceAutoSelect categoryId={6} label="Tipo de problema" editable error={inconvenienceErrors[6]} />
        </CardContent>
      </Card>
    </Stack>
  )
}

export default PhytosanitaryStep
